import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import axios from '../api/axios';
import { imageUrl } from '../utils/imageUrl';

const ProjectPage = () => {
    const { id } = useParams();

    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            try {
                const res = await axios.get(`/gallery/${id}`);
                if (mounted) setProject(res.data.project);
            } catch {
                if (mounted) setError('Project not found.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetch();
        return () => { mounted = false; };
    }, [id]);

    const lightboxPrev = useCallback(() => {
        if (lightbox === null || !project) return;
        setLightbox(lightbox === 0 ? project.images.length - 1 : lightbox - 1);
    }, [lightbox, project]);

    const lightboxNext = useCallback(() => {
        if (lightbox === null || !project) return;
        setLightbox(lightbox === project.images.length - 1 ? 0 : lightbox + 1);
    }, [lightbox, project]);

    useEffect(() => {
        if (lightbox === null) return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') lightboxPrev();
            if (e.key === 'ArrowRight') lightboxNext();
            if (e.key === 'Escape') setLightbox(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightbox, lightboxPrev, lightboxNext]);

    if (loading) return (
        <div className="flex justify-center items-center min-h-screen">
            <div className="w-8 h-8 border-2 border-jade-400 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    if (error || !project) return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-6">
            <p className="font-sans text-stone-400">{error || 'Project not found.'}</p>
            <Link to="/gallery" className="btn-outline">Back to Gallery</Link>
        </div>
    );

    const images = project.images || [];

    return (
        <div>
            {/* ── Hero — first image ── */}
            <div className="relative h-[60vh] md:h-[75vh] w-full bg-cover bg-center"
                style={{ backgroundImage: images[0] ? `url(${imageUrl(images[0].filename)})` : 'none' }}
            >
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/20 to-transparent" />
                <div className="absolute bottom-10 left-8 md:left-16 animate-fade-up">
                    {project.location && (
                        <p className="font-sans text-xs tracking-[0.3em] uppercase text-jade-300 mb-3">
                            {project.location}
                        </p>
                    )}
                    <h1 className="font-serif text-5xl md:text-7xl font-light text-white tracking-wide leading-none">
                        {project.name}
                    </h1>
                    <div className="w-12 h-px bg-jade-400 mt-5" />
                </div>
                <Link
                    to="/gallery"
                    className="absolute top-24 left-8 md:left-16 font-sans text-xs tracking-widest uppercase
                     text-white/60 hover:text-white transition-colors duration-200 flex items-center gap-2"
                >
                    <FaChevronLeft className="w-3 h-3" /> All Projects
                </Link>
            </div>

            {/* ── Description ── */}
            {project.description && (
                <section className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 py-16">
                    <p className="font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-4">
                        About This Project
                    </p>
                    <p className="font-sans text-stone-500 text-base leading-relaxed whitespace-pre-line">
                        {project.description}
                    </p>
                </section>
            )}

            {/* ── Image grid ── */}
            {images.length > 0 && (
                <section className="px-6 md:px-12 lg:px-24 pb-24">
                    <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {images.map((img, i) => (
                            <button
                                key={img._id || img.filename}
                                onClick={() => setLightbox(i)}
                                className="group relative overflow-hidden bg-stone-100 aspect-[4/3] block w-full"
                            >
                                <img
                                    src={imageUrl(img.filename)}
                                    alt={`${project.name} — ${i + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-stone-950/0 group-hover:bg-stone-950/25 transition-all duration-300" />
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* ── Lightbox ── */}
            {lightbox !== null && (
                <div
                    className="fixed inset-0 z-50 bg-stone-950/95 flex items-center justify-center"
                    onClick={() => setLightbox(null)}
                >
                    <img
                        src={imageUrl(images[lightbox].filename)}
                        alt={`${project.name} — ${lightbox + 1}`}
                        className="max-h-[85vh] max-w-[90vw] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button
                        className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
                        onClick={() => setLightbox(null)}
                        aria-label="Close"
                    >
                        <IoCloseOutline className="w-8 h-8" />
                    </button>
                    <button
                        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-3"
                        onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
                        aria-label="Previous"
                    >
                        <FaChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-3"
                        onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
                        aria-label="Next"
                    >
                        <FaChevronRight className="w-6 h-6" />
                    </button>
                    <p className="absolute bottom-6 left-1/2 -translate-x-1/2 font-sans text-xs tracking-widest text-white/40">
                        {lightbox + 1} / {images.length}
                    </p>
                </div>
            )}
        </div>
    );
};

export default ProjectPage;
